import React, { type ReactNode, Suspense } from "react";

import {
  IgFontFamily,
  IgGrayPrimitives,
} from "@intelligems/ig-design-tokens/constants";
import { IgChip } from "@intelligems/ig-theme/IgChip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import LinearProgress from "@mui/material/LinearProgress";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import type { WorkspaceMonacoConfig } from "../../monaco/workspaceMonacoModels";
import type {
  WorkspaceEditorConsoleState,
  WorkspaceEditorState,
} from "../../types";
import { StatusBar } from "../04-StatusBar/StatusBar";
import { EditorConsole } from "./components/EditorConsole";
import { FileSidebar } from "./components/FileSidebar";
import { SyntaxStatus } from "./components/SyntaxStatus";
import { TestFileBanner } from "./components/TestFileBanner";
import {
  suiteOverlayFor,
  TEST_VERDICT_FAILED_CLASS,
  TEST_VERDICT_PASSED_CLASS,
  TEST_VERDICT_SKIPPED_CLASS,
} from "./suiteOverlay";

/** Zone 3 left — the code editor column. */
export interface EditorPanelProps {
  editor: WorkspaceEditorState;
  /** The domain's Monaco model configuration. */
  monaco: WorkspaceMonacoConfig;
  /** Read-only while the entity is live / the user lacks edit rights. */
  readOnly: boolean;
  onChange: (value: string) => void;
  /** Bottom-of-editor console drawer for the latest test run's suite logs. */
  editorConsole: WorkspaceEditorConsoleState;
  /**
   * The workspace controls panel, rendered as the leftmost column below the
   * header row — left of the file list. Passed as a slot rather than built here so
   * the panel's domain wiring stays with the shell that owns it.
   */
  controls?: ReactNode;
  /**
   * Set while an AI author is rewriting the source out-of-band (a chat
   * session building the rule, a repair loop mid-fix). The panel then reads
   * as held rather than editable: the header says who is updating, the code
   * dims, and every file locks — an edit typed now would be overwritten by
   * the next draft the author saves. Null when nothing is writing.
   */
  updatingAuthorName?: string | null;
}

const WorkspaceMonacoEditor = React.lazy(() =>
  import("./WorkspaceMonacoEditor").then((module) => ({
    default: module.WorkspaceMonacoEditor,
  }))
);

/**
 * Zone 3 (left) — the code editor column. Renders:
 *
 *  1. A full-width header row carrying the active filename and the
 *     TypeScript syntax status.
 *  2. Below it, the controls panel slot as the leftmost column, then a file
 *     sidebar beside the Monaco editor (TypeScript ESM), which fills the
 *     remaining height.
 *  3. A status bar: line count · last-saved relative time · dirty indicator.
 *
 * Saving is owned by the workspace top bar; this panel is purely the editing
 * surface. Consumes {@link EditorPanelProps}.
 */
export const EditorPanel = ({
  editor,
  monaco,
  readOnly,
  onChange,
  editorConsole,
  controls,
  updatingAuthorName = null,
}: EditorPanelProps) => {
  const theme = useTheme();
  const editorBackground = theme.palette.background.paper;
  const isUpdating = updatingAuthorName !== null;
  // Held for the author's duration: the buffer is theirs until they finish.
  const isHeld = readOnly || isUpdating;
  const testFiles = editor.testFiles;
  const activeSuite = testFiles?.active ?? null;
  const generatedStatus = testFiles?.generated.status ?? null;
  // The generated file before a run has produced one has nothing to show.
  const showGeneratedPlaceholder =
    activeSuite === "generated" && generatedStatus !== "ready";

  return (
    <Stack
      sx={{ height: "100%", width: "100%", minHeight: 0, p: 1 }}
      spacing={1}
    >
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          border: `1px solid ${theme.igBorders.core.default}`,
          borderRadius: 1.5,
          bgcolor: "background.paper",
          "& .monaco-editor": {
            backgroundColor: `${editorBackground} !important`,
          },
          "& .monaco-editor .margin": {
            backgroundColor: `${editorBackground} !important`,
            borderRight: `1px solid ${theme.igBorders.core.default}`,
          },
          "& .monaco-editor .overflowingContentWidgets, & .monaco-editor .overflowingOverlayWidgets":
            {
              zIndex: theme.zIndex.tooltip,
            },
          [`& .${TEST_VERDICT_PASSED_CLASS}, & .${TEST_VERDICT_FAILED_CLASS}, & .${TEST_VERDICT_SKIPPED_CLASS}`]:
            {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&::before": {
                content: '""',
                width: 8,
                height: 8,
                borderRadius: "50%",
              },
            },
          [`& .${TEST_VERDICT_PASSED_CLASS}::before`]: {
            backgroundColor: theme.palette.success.main,
          },
          [`& .${TEST_VERDICT_FAILED_CLASS}::before`]: {
            backgroundColor: theme.palette.error.main,
          },
          // A ring, not a dot: the run saw the test and made no judgement.
          [`& .${TEST_VERDICT_SKIPPED_CLASS}::before`]: {
            border: `2px solid ${theme.palette.text.disabled}`,
            width: 4,
            height: 4,
          },
        }}
      >
        {/* Full-width header: filename + syntax status. */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{
            minHeight: 36,
            px: 1.5,
            bgcolor: IgGrayPrimitives[200],
            borderBottom: `1px solid ${theme.igBorders.core.default}`,
          }}
        >
          <Stack
            direction="row"
            alignItems="baseline"
            spacing={1}
            sx={{ minWidth: 0 }}
          >
            <IgTypography variant="body2" fontWeight={600} noWrap>
              {editor.activeFile}
            </IgTypography>
            {activeSuite === "generated" ? (
              <IgChip size="small" color="primary" label="AI-generated" />
            ) : null}
            <IgTypography variant="body2" color="text.secondary" noWrap>
              {isHeld || editor.isActiveFileReadOnly ? "Read-only" : "Editable"}{" "}
              · TypeScript
            </IgTypography>
          </Stack>
          {isUpdating ? (
            <Box
              role="status"
              aria-live="polite"
              sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              <IgTypography
                component="span"
                variant="body2"
                fontWeight={600}
                color="text.primary"
              >
                {updatingAuthorName}
              </IgTypography>
              <IgTypography
                component="span"
                variant="body2"
                color="text.secondary"
              >
                {" is updating…"}
              </IgTypography>
            </Box>
          ) : (
            <SyntaxStatus syntax={editor.activeFileSyntax} />
          )}
        </Stack>

        {/* Body: controls column, file list, Monaco. */}
        <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
          {controls}
          <FileSidebar editor={editor} readOnly={isHeld} />
          <Stack
            data-testid="editor-surface"
            aria-busy={isUpdating || undefined}
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflow: "hidden",
              fontFamily: IgFontFamily.code,
              // Monaco's read-only mode prevents edits while preserving scroll,
              // selection, and readable contrast during long-running work.
            }}
          >
            {testFiles !== null ? (
              <TestFileBanner testFiles={testFiles} readOnly={isHeld} />
            ) : null}
            {showGeneratedPlaceholder ? (
              generatedStatus === "loading" ? (
                <LinearProgress aria-label="Loading generated tests" />
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ flex: 1, p: 3 }}
                >
                  <IgTypography
                    variant="body2"
                    color={
                      generatedStatus === "error" ? "error" : "text.secondary"
                    }
                  >
                    {generatedStatus === "error"
                      ? "The generated tests could not be loaded. Reload to try again."
                      : "Generated on your next test run."}
                  </IgTypography>
                </Stack>
              )
            ) : (
              <Suspense
                fallback={<LinearProgress aria-label="Loading code editor" />}
              >
                <WorkspaceMonacoEditor
                  monaco={monaco}
                  value={editor.value}
                  activeFile={editor.activeFile}
                  entryFile={editor.entryFile}
                  files={editor.files}
                  readOnly={isHeld || editor.isActiveFileReadOnly}
                  dependencies={editor.dependencies}
                  suiteOverlay={suiteOverlayFor(editor, isHeld)}
                  reveal={editor.reveal}
                  onChange={(value) => onChange(value)}
                  onValidationChange={(validation, source, modelUri) =>
                    editor.onTypecheckChange(
                      validation,
                      source ?? editor.value,
                      modelUri
                    )
                  }
                />
              </Suspense>
            )}
          </Stack>
        </Stack>

        <Stack
          sx={{
            borderTop: `1px solid ${theme.igBorders.core.default}`,
            bgcolor: "background.paper",
            px: 1.5,
            py: 0.75,
          }}
        >
          <StatusBar editor={editor} />
        </Stack>
      </Stack>

      {editorConsole.isOpen ? <EditorConsole console={editorConsole} /> : null}
    </Stack>
  );
};
